import React, { useState, useEffect, useRef } from "react";
import { useAuthStore } from "../../../store/authStore";
import { useEventStore } from "../../../store/eventStore";
import {
	CalendarIcon,
	XMarkIcon,
	MagnifyingGlassIcon,
	DocumentTextIcon,
	EnvelopeIcon,
	PhoneIcon,
	CheckCircleIcon,
	ClockIcon,
} from "@heroicons/react/24/outline";
import SMSDetailModel from "./SMSDetailModal";

export default function EventSMS() {
	const { user } = useAuthStore();
	const { getSmsLogs, isLoading, error } = useEventStore();
	const isMountedRef = useRef(false);

	// 상태 관리
	const [eventLogs, setEventLogs] = useState([]);
	const [startDate, setStartDate] = useState("");
	const [endDate, setEndDate] = useState("");
	const [localLoading, setLocalLoading] = useState(false);
	const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
	const [selectedEvent, setSelectedEvent] = useState(null);

	// 페이지 로드 시 오늘 날짜로 설정
	useEffect(() => {
		isMountedRef.current = true;
		const controller = new AbortController();
		let today = new Date().toISOString().split("T")[0];
		setStartDate(today);
		setEndDate(today);

		handleSearch(today, today, { signal: controller.signal });

		return () => {
			isMountedRef.current = false;
			controller.abort();
		};
	}, []);

	// 날짜 필터 초기화
	const handleClearDate = (type) => {
		if (type === "start") {
			setStartDate("");
		} else {
			setEndDate("");
		}
	};

	// 검색 버튼 클릭 함수
	const handleSearch = async (_startDate, _endDate, options = {}) => {
		const { signal } = options;
		if (!_startDate || !_endDate) {
			alert("시작일과 종료일을 모두 선택해주세요.");
			return;
		}

		if (signal?.aborted || !isMountedRef.current) return;
		setLocalLoading(true);

		try {
			// API를 통해 SMS 로그 가져오기
			const response = await getSmsLogs(_startDate, _endDate, { signal });
			if (signal?.aborted || !isMountedRef.current) return;

			if (response && response.data) {
				// 행사 데이터 포맷팅
				const formattedEvents = response.data.map((event) => {
					// 출발일 포맷팅 (YYYYMMDD -> YYYY-MM-DD)
					const startDay = event.START_DAY || "";
					const formattedDate =
						startDay.length === 8
							? `${startDay.substring(0, 4)}/${startDay.substring(
									4,
									6
							  )}/${startDay.substring(6, 8)}`
							: startDay;

					// 행사번호 포맷팅
					const eventNumber =
						event.EV_YM && event.EV_SEQ
							? `${event.EV_YM}-${event.EV_SEQ}`
							: "정보 없음";

					return {
						...event,
						formattedDate,
						eventNumber,
						smsStatus:
							event.sms_history && event.sms_history.length > 0
								? "발송성공"
								: "발송 전",
					};
				});

				setEventLogs(formattedEvents);
			} else {
				if (signal?.aborted || !isMountedRef.current) return;
				alert(
					response.error ||
						"행사 정보를 가져오는 중 오류가 발생했습니다."
				);
			}
		} catch (err) {
			const isAbortError =
				err?.name === "AbortError" || err?.code === "ERR_CANCELED";
			if (isAbortError || signal?.aborted || !isMountedRef.current) return;
			console.error("행사 정보 검색 중 오류 발생:", err);
			alert("행사 정보를 가져오는 중 오류가 발생했습니다.");
		} finally {
			if (!signal?.aborted && isMountedRef.current) {
				setLocalLoading(false);
			}
		}
	};

	// 행사 상세 정보 표시 함수
	const handleViewDetail = (event) => {
		// 선택한 행사 정보 저장
		setSelectedEvent(event);
		// 모달 열기
		setIsDetailModalOpen(true);
	};

	// 모달 닫기 함수
	const handleCloseModal = () => {
		setIsDetailModalOpen(false);
		setSelectedEvent(null);
		handleSearch(startDate, endDate);
	};

	return (
		<div className="py-6">
			<h1 className="text-2xl font-bold text-gray-900 mb-6">문자 발송</h1>
			<p className="text-gray-600 mb-4">
				문자 발송 및 결과 조회가 가능한 페이지입니다.
			</p>

			{/* 검색 필터 영역 */}
			<div className="bg-white shadow rounded-lg p-6 mb-6">
				<div className="flex flex-wrap items-center justify-between">
					<div className="flex flex-wrap items-center gap-4">
						{/* 시작 날짜 선택 */}
						<div className="relative">
							<div className="flex items-center">
								<label
									htmlFor="startDate"
									className="mr-2 text-sm font-medium text-gray-700"
								>
									시작일자
								</label>
								<div className="relative">
									<input
										type="date"
										id="startDate"
										value={startDate}
										onChange={(e) => {
											setStartDate(e.target.value);
											setEndDate(e.target.value);
										}}
										className="pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
									/>
									<CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
									{startDate && (
										<button
											onClick={() =>
												handleClearDate("start")
											}
											className="absolute right-2 top-1/2 transform -translate-y-1/2"
										>
											<XMarkIcon className="h-4 w-4 text-gray-400" />
										</button>
									)}
								</div>
							</div>
						</div>

						{/* 종료 날짜 선택 */}
						<div className="relative">
							<div className="flex items-center">
								<label
									htmlFor="endDate"
									className="mr-2 text-sm font-medium text-gray-700"
								>
									종료일자
								</label>
								<div className="relative">
									<input
										type="date"
										id="endDate"
										value={endDate}
										onChange={(e) =>
											setEndDate(e.target.value)
										}
										className="pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
									/>
									<CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
									{endDate && (
										<button
											onClick={() =>
												handleClearDate("end")
											}
											className="absolute right-2 top-1/2 transform -translate-y-1/2"
										>
											<XMarkIcon className="h-4 w-4 text-gray-400" />
										</button>
									)}
								</div>
							</div>
						</div>

						{/* 검색 버튼 */}
						<button
							onClick={() => handleSearch(startDate, endDate)}
							className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md text-sm shadow-sm hover:shadow-md transition duration-150 ease-in-out"
						>
							<div className="flex items-center">
								<MagnifyingGlassIcon className="h-4 w-4 mr-1" />
								검색하기
							</div>
						</button>
					</div>
				</div>
			</div>

			{/* 행사 정보 테이블 */}
			<div className="bg-white shadow rounded-lg overflow-hidden">
				<div className="overflow-x-auto">
					<table className="min-w-full divide-y divide-gray-200">
						<thead className="bg-gray-50">
							<tr>
								<th
									scope="col"
									className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
								>
									행사번호
								</th>
								<th
									scope="col"
									className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
								>
									출발일자
								</th>
								<th
									scope="col"
									className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
								>
									행사명
								</th>
								<th
									scope="col"
									className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
								>
									발송상태
								</th>
								<th
									scope="col"
									className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
								>
									상세보기
								</th>
							</tr>
						</thead>
						<tbody
							className="bg-white divide-y divide-gray-200"
							id="main-tbody"
						>
							{eventLogs.length > 0 ? (
								eventLogs.map((event, index) => (
									<tr
										key={event.eventNumber || index}
										className="hover:bg-gray-50"
									>
										<td className="px-4 py-3 text-sm text-gray-900">
											{event.eventNumber}
										</td>
										<td className="px-4 py-3 text-sm text-gray-900">
											{event.formattedDate}
										</td>
										<td className="px-4 py-3 text-sm text-gray-900">
											<div className="w-full">
												{event.EV_NM || "정보 없음"}
											</div>
										</td>
										<td className="px-4 py-3 text-sm">
											<div className="flex items-center">
												{event.smsStatus ===
												"발송성공" ? (
													<div className="flex items-center space-x-1.5 text-emerald-600">
														<CheckCircleIcon className="h-5 w-5" />
														<span className="font-medium">
															발송완료
														</span>
													</div>
												) : (
													<div className="flex items-center space-x-1.5 text-amber-500">
														<ClockIcon className="h-5 w-5" />
														<span className="font-medium">
															발송 대기
														</span>
													</div>
												)}
											</div>
										</td>
										<td className="px-2 py-3 text-sm font-medium">
											<button
												onClick={() =>
													handleViewDetail(event)
												}
												className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
											>
												<svg
													className="w-3 h-3 mr-1"
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
													xmlns="http://www.w3.org/2000/svg"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth="2"
														d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
													></path>
												</svg>
												상세보기
											</button>
										</td>
									</tr>
								))
							) : (
								<tr>
									<td
										colSpan="5"
										className="px-4 py-8 text-center text-gray-500"
									>
										{localLoading ? (
											<div className="flex justify-center items-center">
												<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
												<span className="ml-2">
													로딩 중...
												</span>
											</div>
										) : (
											<div className="flex flex-col items-center">
												<EnvelopeIcon className="h-10 w-10 text-gray-400 mb-2" />
												<p>
													조회된 행사 정보가 없습니다.
												</p>
												<p className="text-sm">
													날짜를 선택하고 검색해
													주세요.
												</p>
											</div>
										)}
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			</div>

			{/* SMS 상세 모달 */}
			<SMSDetailModel
				isOpen={isDetailModalOpen}
				onClose={handleCloseModal}
				eventData={selectedEvent}
			/>
		</div>
	);
}
